import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/react";
import { withSpan, addBreadcrumb, captureException } from "../utils/sentry";

// Helper to check if we're in React Native WebView
const isReactNativeWebView = () => {
  return /reactnative/i.test(navigator.userAgent);
};

export const useBluetooth = ({ printerConnectCallback = null }) => {
  const [status, setStatus] = useState("Unknown");
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    // Set up message listener
    const handleMessage = (event) => {
      try {
        const { type, payload } = JSON.parse(event.data);
        
        // Add breadcrumb for bluetooth events
        addBreadcrumb(`Bluetooth event: ${type}`, "bluetooth.event", {
          type,
          hasPayload: !!payload,
        });

        switch (type) {
          case "getBluetoothStatusResult":
            setStatus(payload.result);
            break;
          case "scanDevicesResult":
            setDevices(
              payload.devices.map((device) => ({
                ...device,
                isConnected: false,
                connectionError: "",
              })) || []
            );
            setIsScanning(false);
            break;
          case "connectPrinterResult":
            if (payload.result === "SUCCESS") {
              setDevices((prevDevices) =>
                prevDevices.map((device) =>
                  device.innerMacAddress === payload.printer.innerMacAddress
                    ? { ...device, isConnected: true }
                    : device
                )
              );
              printerConnectCallback &&
                printerConnectCallback(
                  { ...payload.printer, isConnected: true },
                  payload.result === "SUCCESS"
                );
              setError(null);
            } else {
              setDevices((prevDevices) =>
                prevDevices.map((device) =>
                  device.innerMacAddress === payload.printer.innerMacAddress
                    ? { ...device, connectionError: payload.error }
                    : device
                )
              );
              printerConnectCallback &&
                printerConnectCallback(
                  { ...payload.printer, isConnected: false },
                  payload.result === "SUCCESS"
                );
            }
            break;
          case "printResult":
            if (payload.result === "FAILURE") {
              setError(payload.error || "Failed to print");
            } else {
              setError(null);
            }
            break;
          case "askPermissionResult":
            const permissionResult = payload.result;
            if (permissionResult === "PERMISSION_DENIED") {
              setError("Bluetooth permission denied");
            } else if (permissionResult === "PERMISSION_ERROR") {
              setError("Error requesting Bluetooth permission");
            } else if (permissionResult === "PERMISSION_GRANTED") {
              setError(null);
            }
            break;
          case "enableBluetoothResult":
            if (payload.result === "BLUETOOTH_REQUEST_DENIED") {
              setError("Bluetooth enable request denied");
            } else if (payload.result === "BLUETOOTH_SETTINGS_OPENED") {
              // Settings opened, wait for user action
            }
            break;
          case "disableBluetoothResult":
            if (payload.result === "BLUETOOTH_REQUEST_DENIED") {
              setError("Bluetooth disable request denied");
            }
            break;
        }
      } catch (err) {
        console.error("Error handling message:", err);
        captureException(err, {
          tags: { component: "useBluetooth", action: "handleMessage" },
          extra: { eventType: event?.data ? JSON.parse(event.data)?.type : "unknown" },
        });
      }
    };

    if (isReactNativeWebView()) {
      window.addEventListener("message", handleMessage);
      return () => window.removeEventListener("message", handleMessage);
    }
  }, []);

  const sendCommand = useCallback((type, payload = {}) => {
    return withSpan(
      {
        name: `Bluetooth Send Command: ${type}`,
        op: "bluetooth.command",
        function: `sendCommand_${type}`,
        attributes: {
          "bluetooth.command.type": type,
          "bluetooth.command.has_payload": !!payload,
        },
      },
      async (span) => {
        if (!isReactNativeWebView()) {
          console.warn("Not in React Native WebView environment");
          span?.setAttribute("bluetooth.environment", "web");
          printerConnectCallback && printerConnectCallback(payload, false);
          return;
        }
        
        span?.setAttribute("bluetooth.environment", "react-native-webview");
        const message = {
          id: Date.now().toString(),
          type,
          payload,
        };
        
        addBreadcrumb(`Sending Bluetooth command: ${type}`, "bluetooth.command", {
          commandType: type,
          messageId: message.id,
        });
        
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      }
    );
  }, []);

  const requestPermission = useCallback(() => {
    return withSpan(
      {
        name: "Bluetooth Request Permission",
        op: "bluetooth.permission",
        function: "requestPermission",
      },
      async (span) => {
        addBreadcrumb("Requesting Bluetooth permission", "bluetooth.permission");
        await sendCommand("askPermission");
        span?.setStatus("ok");
      }
    );
  }, [sendCommand]);

  const getBluetoothStatus = useCallback(() => {
    sendCommand("getBluetoothStatus");
  }, [sendCommand]);

  const enableBluetooth = useCallback(() => {
    sendCommand("enableBluetooth");
  }, [sendCommand]);

  const disableBluetooth = useCallback(() => {
    sendCommand("disableBluetooth");
  }, [sendCommand]);

  const openSettings = useCallback(() => {
    sendCommand("openSettingsForBLE");
  }, [sendCommand]);

  const startScan = useCallback(() => {
    return withSpan(
      {
        name: "Bluetooth Start Scan",
        op: "bluetooth.scan",
        function: "startScan",
      },
      async (span) => {
        setIsScanning(true);
        setDevices([]);
        addBreadcrumb("Starting Bluetooth device scan", "bluetooth.scan");
        await sendCommand("scanDevices");
        span?.setStatus("ok");
      }
    );
  }, [sendCommand]);

  const connectPrinter = useCallback(
    (printer) => {
      return withSpan(
        {
          name: "Bluetooth Connect Printer",
          op: "bluetooth.connect",
          function: "connectPrinter",
          attributes: {
            "bluetooth.printer.mac": printer?.innerMacAddress || "unknown",
            "bluetooth.printer.name": printer?.name || "unknown",
          },
        },
        async (span) => {
          if (!printer.innerMacAddress) {
            span?.setStatus("invalid_argument");
            span?.setAttribute("error.message", "Missing innerMacAddress");
            return;
          }
          
          addBreadcrumb("Connecting to Bluetooth printer", "bluetooth.connect", {
            macAddress: printer.innerMacAddress,
            printerName: printer.name,
          });
          
          await sendCommand("connectPrinter", printer);
          setSelectedDevice(printer.innerMacAddress);
          span?.setStatus("ok");
        }
      );
    },
    [sendCommand]
  );

  const print = useCallback(
    (content, config = {}) => {
      return withSpan(
        {
          name: "Bluetooth Print",
          op: "bluetooth.print",
          function: "print",
          attributes: {
            "bluetooth.print.content_length": content?.length || 0,
            "bluetooth.print.has_config": !!config,
          },
        },
        async (span) => {
          const defaultConfig = {
            beep: true,
            cut: true,
            tailingLine: true,
            encoding: "UTF-8",
            codepage: 0,
          };

          const printMsg = `<Printout>${content}</Printout>`;

          addBreadcrumb("Sending print command to Bluetooth printer", "bluetooth.print", {
            contentLength: content?.length || 0,
          });

          await sendCommand("print", {
            printText: printMsg,
            options: defaultConfig,
          });
          
          span?.setStatus("ok");
        }
      );
    },
    [sendCommand]
  );

  // Helper function to create formatted text
  const createTextElement = useCallback(
    ({
      text,
      align = "left",
      fontWidth = 1,
      fontHeight = 1,
      bold = 0,
      base64 = 0,
    }) => {
      return `<Text align="${align}" fontWidth="${fontWidth}" fontHeight="${fontHeight}" bold="${bold}" base64="${base64}">${text}</Text>`;
    },
    []
  );

  // Helper function to create QR code
  const createQRCode = useCallback(
    ({ content, version = 0, errorCorrectionLevel = 3, magnification = 6 }) => {
      return `<QRCode version="${version}" errorCorrectionLevel="${errorCorrectionLevel}" magnification="${magnification}">${content}</QRCode>`;
    },
    []
  );

  return {
    status,
    devices,
    selectedDevice,
    isConnected,
    error,
    isScanning,
    requestPermission,
    getBluetoothStatus,
    enableBluetooth,
    disableBluetooth,
    openSettings,
    startScan,
    connectPrinter,
    print,
    createTextElement,
    createQRCode,
  };
};
